import { rgbAt } from '../image/analysis.js';
import { EYE_A_CONTOUR, EYE_B_CONTOUR, centroid, pick } from '../geometry/landmarks.js';
import type { NormalizedLandmark, NormalizedPoint, NormalizedRect } from '../types/geometry.js';
import { clamp01, roundTo } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import { pixelBounds } from './hairOcclusion.js';

/**
 * Red-eye analysis for the «Красные глаза» check.
 *
 * The scan is confined to the iris/pupil ROI built from the eye centres and the
 * eye-corner distance — never the whole image, never the skin, never the
 * eyelids or a red frame. A pixel only counts when it is inside the ROI *and*
 * clearly red-dominant; isolated pixels never count, only connected clusters.
 * That combination is what keeps lipstick, blush and a red frame from ever
 * producing a finding.
 */

export interface RedEyeRoi {
  id: 'left_eye' | 'right_eye';
  center: NormalizedPoint;
  rect: NormalizedRect;
  /** Pixels of the ROI ellipse that were actually read. */
  pixelCount: number;
  /** Red-dominant pixels that belong to a cluster of the minimum size. */
  clusteredPixels: number;
}

export interface RedEyeParams {
  minRedDominance: number;
  minRedSaturation: number;
  minRedPixelRatio: number;
  maxGlassesRedRatio: number;
  minClusterSize: number;
  minRoiPixels: number;
}

export type RedEyeStatus = 'clear' | 'suspected' | 'roi_too_small' | 'suspicious_large';

export interface RedEyeAnalysis {
  status: RedEyeStatus;
  /** Clustered red pixels over ROI pixels, 0..1. */
  redPixelRatio: number;
  /** All red-dominant pixels (including isolated ones) over ROI pixels. */
  rawRedPixelRatio: number;
  clusterCount: number;
  largestCluster: number;
  roiPixels: number;
  rois: RedEyeRoi[];
  confidence: number;
  notes: string[];
}

export const DEFAULT_RED_EYE_PARAMS: RedEyeParams = {
  minRedDominance: 55,
  minRedSaturation: 0.45,
  minRedPixelRatio: 0.015,
  maxGlassesRedRatio: 0.35,
  minClusterSize: 4,
  minRoiPixels: 40,
};

interface RoiGeometry {
  id: 'left_eye' | 'right_eye';
  center: NormalizedPoint;
  rx: number;
  ry: number;
}

const contourWidth = (
  landmarks: readonly NormalizedLandmark[],
  contour: readonly number[],
): number => {
  const a = pick(landmarks, contour[0] as number);
  const b = pick(landmarks, contour[3] as number);
  if (!a || !b) return 0;
  return Math.hypot(a.x - b.x, a.y - b.y);
};

/** Eye widths paired with the geometry's left/right eye centres. */
const eyeWidths = (
  landmarks: readonly NormalizedLandmark[],
  geometry: FaceGeometryMetrics | undefined,
): { left: number; right: number } => {
  const widthA = contourWidth(landmarks, EYE_A_CONTOUR);
  const widthB = contourWidth(landmarks, EYE_B_CONTOUR);
  const centerA = centroid(landmarks, EYE_A_CONTOUR);
  const centerB = centroid(landmarks, EYE_B_CONTOUR);
  if (!centerA || !centerB || widthA <= 0 || widthB <= 0) {
    const fallback = geometry ? Math.max(geometry.eyeDistance * 0.35, 0.02) : 0.02;
    return { left: fallback, right: fallback };
  }
  return centerA.x <= centerB.x
    ? { left: widthA, right: widthB }
    : { left: widthB, right: widthA };
};

const buildRois = (
  geometry: FaceGeometryMetrics,
  landmarks: readonly NormalizedLandmark[],
): RoiGeometry[] => {
  const widths = eyeWidths(landmarks, geometry);
  const make = (
    id: 'left_eye' | 'right_eye',
    center: NormalizedPoint,
    width: number,
  ): RoiGeometry => {
    const safeWidth = width > 0 ? width : Math.max(geometry.eyeDistance * 0.35, 0.02);
    return { id, center, rx: safeWidth * 0.5, ry: safeWidth * 0.38 };
  };
  return [
    make('left_eye', geometry.eyeLeft, widths.left),
    make('right_eye', geometry.eyeRight, widths.right),
  ];
};

const clusterRedPixels = (
  red: Uint8Array,
  boundsWidth: number,
  boundsHeight: number,
  minClusterSize: number,
): { clustered: number; clusters: number; largest: number; raw: number } => {
  const visited = new Uint8Array(red.length);
  let raw = 0;
  for (let index = 0; index < red.length; index += 1) if (red[index] === 1) raw += 1;

  let clustered = 0;
  let clusters = 0;
  let largest = 0;
  const stack: number[] = [];
  for (let start = 0; start < red.length; start += 1) {
    if (red[start] !== 1 || visited[start] === 1) continue;
    stack.length = 0;
    stack.push(start);
    visited[start] = 1;
    let size = 0;
    while (stack.length > 0) {
      const current = stack.pop() as number;
      size += 1;
      const x = current % boundsWidth;
      const y = Math.floor(current / boundsWidth);
      const neighbours = [
        x > 0 ? current - 1 : -1,
        x < boundsWidth - 1 ? current + 1 : -1,
        y > 0 ? current - boundsWidth : -1,
        y < boundsHeight - 1 ? current + boundsWidth : -1,
      ];
      for (const neighbour of neighbours) {
        if (neighbour < 0 || red[neighbour] !== 1 || visited[neighbour] === 1) continue;
        visited[neighbour] = 1;
        stack.push(neighbour);
      }
    }
    if (size >= minClusterSize) {
      clustered += size;
      clusters += 1;
      if (size > largest) largest = size;
    }
  }
  return { clustered, clusters, largest, raw };
};

export interface RedEyeAnalysisInput {
  pixels: AnalysisBuffer;
  geometry: FaceGeometryMetrics;
  landmarks?: readonly NormalizedLandmark[];
  params?: Partial<RedEyeParams>;
}

export const analyseRedEye = (input: RedEyeAnalysisInput): RedEyeAnalysis => {
  const params: RedEyeParams = { ...DEFAULT_RED_EYE_PARAMS, ...(input.params ?? {}) };
  const { pixels, geometry } = input;
  const landmarks = input.landmarks ?? geometry.landmarks;
  const { data, width, height, channels } = pixels;
  const notes: string[] = [];

  const rois = buildRois(geometry, landmarks);
  const roiResults: RedEyeRoi[] = [];
  let totalRoiPixels = 0;
  let totalClustered = 0;
  let totalRaw = 0;
  let clusterCount = 0;
  let largestCluster = 0;

  for (const roi of rois) {
    const rect: NormalizedRect = {
      x: roi.center.x - roi.rx,
      y: roi.center.y - roi.ry,
      width: roi.rx * 2,
      height: roi.ry * 2,
    };
    const { x0, y0, x1, y1 } = pixelBounds(rect, width, height);
    const boundsWidth = Math.max(0, x1 - x0);
    const boundsHeight = Math.max(0, y1 - y0);
    const red = new Uint8Array(boundsWidth * boundsHeight);
    let roiPixels = 0;

    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const nx = (x + 0.5) / width;
        const ny = (y + 0.5) / height;
        const dx = (nx - roi.center.x) / Math.max(roi.rx, 1e-6);
        const dy = (ny - roi.center.y) / Math.max(roi.ry, 1e-6);
        if (dx * dx + dy * dy > 1) continue;
        roiPixels += 1;
        const colour = rgbAt(data, width, channels, x, y);
        const max = Math.max(colour.r, colour.g, colour.b);
        const min = Math.min(colour.r, colour.g, colour.b);
        const dominance = colour.r - Math.max(colour.g, colour.b);
        const saturation = max > 0 ? (max - min) / max : 0;
        if (
          dominance >= params.minRedDominance &&
          saturation >= params.minRedSaturation &&
          colour.r >= 60
        ) {
          red[(y - y0) * boundsWidth + (x - x0)] = 1;
        }
      }
    }

    const clusteredResult = clusterRedPixels(red, boundsWidth, boundsHeight, params.minClusterSize);
    totalRoiPixels += roiPixels;
    totalClustered += clusteredResult.clustered;
    totalRaw += clusteredResult.raw;
    clusterCount += clusteredResult.clusters;
    if (clusteredResult.largest > largestCluster) largestCluster = clusteredResult.largest;

    roiResults.push({
      id: roi.id,
      center: { x: roundTo(roi.center.x, 4), y: roundTo(roi.center.y, 4) },
      rect: {
        x: roundTo(rect.x, 4),
        y: roundTo(rect.y, 4),
        width: roundTo(rect.width, 4),
        height: roundTo(rect.height, 4),
      },
      pixelCount: roiPixels,
      clusteredPixels: clusteredResult.clustered,
    });
  }

  const redPixelRatio = totalRoiPixels > 0 ? totalClustered / totalRoiPixels : 0;
  const rawRedPixelRatio = totalRoiPixels > 0 ? totalRaw / totalRoiPixels : 0;

  const adequacy = clamp01(totalRoiPixels / (params.minRoiPixels * 6));
  const confidence = clamp01(0.45 + 0.35 * adequacy + (redPixelRatio > 0 ? 0.1 : 0.05));

  let status: RedEyeStatus = 'clear';
  if (totalRoiPixels < params.minRoiPixels) {
    status = 'roi_too_small';
    notes.push('Область зрачков слишком мала в анализируемом буфере.');
  } else if (redPixelRatio > params.maxGlassesRedRatio) {
    status = 'suspicious_large';
    notes.push('Красная область слишком велика для red-eye: вероятно, это не зрачок.');
  } else if (redPixelRatio > params.minRedPixelRatio && clusterCount > 0) {
    status = 'suspected';
  }

  return {
    status,
    redPixelRatio: roundTo(redPixelRatio, 4),
    rawRedPixelRatio: roundTo(rawRedPixelRatio, 4),
    clusterCount,
    largestCluster,
    roiPixels: totalRoiPixels,
    rois: roiResults,
    confidence: roundTo(confidence, 3),
    notes,
  };
};
